import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dietService } from '../../services/dietService';
import { lookupTableService } from '../../services/lookupTableService';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import DeleteConfirmModal from '../../components/UI/DeleteConfirmModal';
import RecipePlateModal from '../../components/Setup/RecipePlateModal';
import { MagnifyingGlassIcon, PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { RecipePlate, RecipePlateProfile } from '../../types/diet';
import { ColumnDef } from '@tanstack/react-table';
import DataGrid from '../../components/DataGrid';
import { useToast } from '../../components/UI/ToastContainer';

const RecipePlatesPage: React.FC = () => {
  // Profiles (left menu)
  const [selectedProfile, setSelectedProfile] = useState<RecipePlateProfile | null>(null);
  const [isCreateProfileOpen, setIsCreateProfileOpen] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<RecipePlateProfile | null>(null);
  const [profileSearch, setProfileSearch] = useState('');
  const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(false);

  // Items (right panel)
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('plateSize');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<RecipePlate | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<RecipePlate | null>(null);
  const [confirmDeleteProfile, setConfirmDeleteProfile] = useState<RecipePlateProfile | null>(null);
  const pageSize = 50;
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<{ name?: string; status?: string }>({});

  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToast();

  // Load profiles
  const { data: profileData, isLoading: loadingProfiles } = useQuery({
    queryKey: ['recipe-plate-profiles', profileSearch],
    queryFn: () => dietService.getRecipePlateProfiles({ search: profileSearch }),
  });

    // Load items bound to selected profile
  const { data, isLoading } = useQuery({
    queryKey: ['recipe-plates', selectedProfile?.id, currentPage, searchTerm, sortBy, sortOrder, filters],
    enabled: !!selectedProfile?.id,
    queryFn: () => dietService.getRecipePlates({ page: currentPage, limit: pageSize, search: searchTerm, sortBy, sortOrder, name: filters.name, status: filters.status, profileId: selectedProfile?.id }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dietService.deleteRecipePlate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe-plates'] });
      setConfirmDelete(null);
      showSuccess('Recipe plate deleted', 'The recipe plate has been successfully deleted.');
    },
    onError: (error: any) => {
      const message = error?.message || 'Failed to delete recipe plate. Please try again.';
      showError('Delete failed', message);
    },
  });

  const deleteProfileMutation = useMutation({
    mutationFn: (id: string) => dietService.deleteRecipePlateProfile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe-plate-profiles'] });
      queryClient.invalidateQueries({ queryKey: ['recipe-plates'] });
      setConfirmDeleteProfile(null);
      setSelectedProfile(null);
      showSuccess('Profile deleted', 'The recipe plate profile and all its items have been deleted.');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to delete profile. Please try again.';
      showError('Delete failed', message);
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: (data: { id: string; name: string; dietId?: string; kitchenSectionId?: string; status: 'ACTIVE' | 'INACTIVE' }) =>
      dietService.updateRecipePlateProfile(data.id, { name: data.name, dietId: data.dietId, kitchenSectionId: data.kitchenSectionId, status: data.status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe-plate-profiles'] });
      setIsEditProfileOpen(false);
      setEditingProfile(null);
      showSuccess('Profile updated', 'The recipe plate profile has been updated.');
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || 'Failed to update profile. Please try again.';
      showError('Update failed', message);
    },
  });



  const handleSort = (column: string) => {
    if (sortBy === column) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortBy(column); setSortOrder('asc'); }
  };

  const handleExport = async () => {
    try {
      const blob = await dietService.exportRecipePlates({
        search: searchTerm,
        name: filters.name,
        status: filters.status,
        ids: selectedIds.length ? selectedIds : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.style.display = 'none'; a.href = url; a.download = `recipe-plates-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url);
    } catch (err) { console.error('Export failed', err); }
  };

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const getStatusColor = (s?: string) => (s === 'ACTIVE' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800');
  const handleBulkAction = async (status: 'ACTIVE' | 'INACTIVE') => {
    if (!selectedIds.length) return;
    try {
      await dietService.bulkUpdateRecipePlateStatus(selectedIds, status);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['recipe-plates'] });
    } catch (e: any) {
      showError('Bulk action failed', e?.message || '');
    }
  };



  const openCreate = () => { setSelected(null); setIsCreateProfileOpen(true); };
  const openEdit = useCallback((rp: RecipePlate) => { setSelected(rp); setIsModalOpen(true); }, []);

  const clearFilters = () => { setFilters({}); setSearchTerm(''); setCurrentPage(1); };


  const columns = useMemo<ColumnDef<RecipePlate>[]>(() => [
    // Hidden by default, used for grouping by plate Description
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Description',
      cell: (info) => String(info.getValue() ?? '-'),
    },
    {
      id: 'plateSize',
      header: 'Plate Size',
      meta: { sortable: true },
      cell: ({ row }) => {
        const size = (row.original as any).size ?? 'xs';
        const sizeMap: Record<string, string> = {
          'xs': 'X Small',
          's': 'Small',
          'm': 'Medium',
          'l': 'Large',
          'xl': 'Extra Large',
          'xxl': 'XX Large',
        };
        return sizeMap[size] || size;
      },
    },
    {
      id: 'itemType',
      header: 'Item Type',
      cell: ({ row }) => {
        const r: any = row.original as any;
        return (r.recipeId || r.recipe) ? 'Recipe' : (r.ingredientId || r.ingredient) ? 'Ingredient' : '-';
      },
    },
    {
      id: 'item',
      header: 'Item',
      cell: ({ row }) => (row.original as any).recipe?.name || (row.original as any).ingredient?.name || '-',
    },
    {
      id: 'calories',
      header: 'Calories',
      meta: { sortable: true },
      cell: ({ row }) => (row.original as any).calories ?? '-',
    },
    {
      id: 'protein',
      header: 'Protein (g)',
      meta: { sortable: true },
      cell: ({ row }) => (row.original as any).protein ?? '-',
    },
    {
      id: 'carbs',
      header: 'Carbs (g)',
      meta: { sortable: true },
      cell: ({ row }) => (row.original as any).carbs ?? '-',
    },
    {
      id: 'fat',
      header: 'Fat (g)',
      meta: { sortable: true },
      cell: ({ row }) => (row.original as any).fat ?? '-',
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor((row.original as any).status)}`}>
          {(row.original as any).status}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-end space-x-2">
          <button onClick={() => openEdit(row.original as RecipePlate)} className="text-primary-600 hover:text-primary-900">
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button onClick={() => setConfirmDelete(row.original as RecipePlate)} className="text-red-600 hover:text-red-900">
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ], [openEdit, setConfirmDelete]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Recipe Plates</h1>
        <div className="flex items-center space-x-3">
          <Button onClick={openCreate}><PlusIcon className="w-5 h-5 mr-2" />Create Profile</Button>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Left: Profiles list */}
        <div className="col-span-12 md:col-span-4">
          <div className="bg-white rounded-lg shadow-soft p-4 h-full">
            <div className="mb-3 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input type="text" placeholder="Search profiles..." className="pl-10 form-input" value={profileSearch} onChange={(e)=>setProfileSearch(e.target.value)} />
            </div>
            {loadingProfiles ? (
              <div className="py-10 flex justify-center"><LoadingSpinner /></div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {(profileData?.profiles || []).map((p) => (
                  <li key={p.id} className={`py-3 px-2 rounded ${selectedProfile?.id===p.id ? 'bg-gray-50' : ''}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 cursor-pointer" onClick={()=>{ setSelectedProfile(p); setCurrentPage(1); }}>
                        <div className="font-medium text-gray-900">{p.name}</div>
                        <div className="text-xs text-gray-500">{p.kitchenSection?.value || ''} {p.diet?.name ? `• ${p.diet.name}` : ''}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-xs text-gray-500 whitespace-nowrap">{p.itemsCount ?? 0} items</div>
                        <button
                          onClick={(e) => { e.stopPropagation(); setEditingProfile(p); setIsEditProfileOpen(true); }}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Edit profile"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteProfile(p); }}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete profile"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right: Items table for selected profile */}
        <div className="col-span-12 md:col-span-8">
          <div className="bg-white rounded-lg shadow-soft p-4">
            {!selectedProfile ? (
              <div className="py-16 text-center text-gray-500">Select a profile to manage its items.</div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-lg font-semibold">{selectedProfile.name}</div>
                    <div className="text-sm text-gray-500">{selectedProfile.kitchenSection?.value || ''} {selectedProfile.diet?.name ? `• ${selectedProfile.diet.name}` : ''}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setIsViewDetailsOpen(true)}>View Details</Button>
                    <Button variant="secondary" size="sm" onClick={() => setIsModalOpen(true)}>Manage Items</Button>
                    <Button variant="outline" size="sm" disabled={!selectedIds.length} onClick={() => handleBulkAction('ACTIVE')}>Activate</Button>
                    <Button variant="outline" size="sm" disabled={!selectedIds.length} onClick={() => handleBulkAction('INACTIVE')}>Deactivate</Button>
                    <Button variant="secondary" size="sm" onClick={handleExport}>Export</Button>
                  </div>
                </div>

                {isLoading ? (
                  <div className="flex items-center justify-center py-12"><LoadingSpinner size="lg" /></div>
                ) : (
                  <>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3">
                      <input
                        value={filters.name || ''}
                        onChange={(e) => { setFilters((f) => ({ ...f, name: e.target.value || undefined })); setCurrentPage(1); }}
                        placeholder="Filter item"
                        className="form-input form-input-sm"
                        type="text"
                      />
                      <select
                        className="form-input form-input-sm"
                        value={filters.status ?? ''}
                        onChange={(e) => { setFilters((f) => ({ ...f, status: e.target.value || undefined })); setCurrentPage(1); }}
                      >
                        <option value="">All statuses</option>
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                      </select>
                      <div className="flex items-center">
                        <Button variant="secondary" size="sm" onClick={clearFilters}>Clear</Button>
                      </div>
                    </div>
                    <DataGrid
                      data={data?.recipePlates ?? []}
                      columns={columns}
                      getRowId={(r) => (r as RecipePlate).id}
                      enableSelection
                      selectedIds={selectedIds}
                      onToggleRow={(id) => toggleRow(id)}
                      onToggleAll={(ids, checked) =>
                        setSelectedIds((prev) => (checked ? Array.from(new Set([...prev, ...ids])) : prev.filter((x) => !ids.includes(x))))
                      }
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSortChange={handleSort}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {isModalOpen && selectedProfile && (
        <RecipePlateModal
          plate={selected}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            setIsModalOpen(false);
            queryClient.invalidateQueries({ queryKey: ['recipe-plates'] });
            queryClient.invalidateQueries({ queryKey: ['recipe-plate-profiles'] });
            queryClient.invalidateQueries({ queryKey: ['recipe-plate-profile-items', selectedProfile.id] });
          }}
          prefill={{ desc: selectedProfile.name, dietId: selectedProfile.dietId, ksId: selectedProfile.kitchenSectionId }}
          lockTopFields={Boolean(selectedProfile.dietId && selectedProfile.kitchenSectionId)}
          profileId={selectedProfile.id}
        />
      )}

      {confirmDelete && (
        <DeleteConfirmModal
          title="Delete Recipe Plate"
          message={`Are you sure you want to delete "${confirmDelete.name}"? This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => deleteMutation.mutate(confirmDelete.id)}
          loading={deleteMutation.isPending}
        />
      )}

      {confirmDeleteProfile && (
        <DeleteConfirmModal
          title="Delete Recipe Plate Profile"
          message={`Are you sure you want to delete the profile "${confirmDeleteProfile.name}" and all its items? This action cannot be undone.`}
          onCancel={() => setConfirmDeleteProfile(null)}
          onConfirm={() => deleteProfileMutation.mutate(confirmDeleteProfile.id)}
          loading={deleteProfileMutation.isPending}
        />
      )}

      {isCreateProfileOpen && (
        <CreateProfileModal
          onClose={() => setIsCreateProfileOpen(false)}
          onSuccess={() => { setIsCreateProfileOpen(false); queryClient.invalidateQueries({ queryKey: ['recipe-plate-profiles'] }); }}
        />
      )}

      {isEditProfileOpen && editingProfile && (
        <EditProfileModal
          profile={editingProfile}
          onClose={() => { setIsEditProfileOpen(false); setEditingProfile(null); }}
          onSuccess={() => { setIsEditProfileOpen(false); setEditingProfile(null); queryClient.invalidateQueries({ queryKey: ['recipe-plate-profiles'] }); setSelectedProfile(null); }}
          mutation={updateProfileMutation}
        />
      )}

      {isViewDetailsOpen && selectedProfile && (
        <ViewDetailsModal
          profile={selectedProfile}
          onClose={() => setIsViewDetailsOpen(false)}
        />
      )}
    </div>
  );
};

// Edit Profile Modal
const EditProfileModal: React.FC<{ profile: RecipePlateProfile; onClose: () => void; onSuccess: () => void; mutation: any }> = ({ profile, onClose, onSuccess, mutation }) => {
  const [name, setName] = useState(profile.name);
  const [dietId, setDietId] = useState(profile.dietId || '');
  const [ksId, setKsId] = useState(profile.kitchenSectionId || '');
  const [status, setStatus] = useState<'ACTIVE'|'INACTIVE'>((profile.status as 'ACTIVE'|'INACTIVE') || 'ACTIVE');
  const [error, setError] = useState<string | null>(null);

  const { data: dietsData } = useQuery({
    queryKey: ['diets', 'for-select'],
    queryFn: () => dietService.getDiets({ page: 1, limit: 1000, status: 'ACTIVE' as any }),
  });
  const { data: ksData } = useQuery({
    queryKey: ['lookup', 'kitchen-sections'],
    queryFn: () => lookupTableService.getLookupTables({ page: 1, limit: 1000, category: 'KITCHEN_SECTION', status: 'ACTIVE' }),
  });

  const diets = (dietsData as any)?.diets || [];
  const kitchenSections = (ksData as any)?.lookupTables || [];

  const handleSave = () => {
    setError(null);
    if (!name.trim()) { setError('Name is required'); return; }
    mutation.mutate({ id: profile.id, name: name.trim(), dietId: dietId || undefined, kitchenSectionId: ksId || undefined, status }, {
      onSuccess,
      onError: (e: any) => setError(e?.response?.data?.message || 'Failed to update profile'),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Edit Recipe Plate Profile</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        {error && <div className="mb-3 rounded bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        <div className="space-y-4">
          <div>
            <label className="form-label">Name *</label>
            <input className="form-input" value={name} onChange={e=>setName(e.target.value)} placeholder="Profile name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Diet</label>
              <select className="form-input" value={dietId} onChange={e=>setDietId(e.target.value)}>
                <option value="">Select diet</option>
                {diets.map((d: any) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Kitchen Section</label>
              <select className="form-input" value={ksId} onChange={e=>setKsId(e.target.value)}>
                <option value="">Select kitchen section</option>
                {kitchenSections.map((k: any) => <option key={k.id} value={k.id}>{k.value}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="form-label">Status</label>
            <select className="form-input" value={status} onChange={e=>setStatus(e.target.value as any)}>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button onClick={handleSave} loading={mutation.isPending}>Update</Button>
        </div>
      </div>
    </div>
  );
};

// View Details Modal
const ViewDetailsModal: React.FC<{ profile: RecipePlateProfile; onClose: () => void }> = ({ profile, onClose }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['recipe-plate-profile-items', profile.id],
    queryFn: () => dietService.getRecipePlateProfileItems(profile.id),
  });

  const items = (data as any)?.items || [];

  // Group items by recipe/ingredient
  const groupedItems = useMemo(() => {
    const groups = new Map<string, any[]>();
    items.forEach((item: any) => {
      const key = item.recipeId || item.ingredientId;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(item);
    });
    return Array.from(groups.values());
  }, [items]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-6xl max-h-[90vh] overflow-auto rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4 sticky top-0 bg-white">
          <div>
            <h3 className="text-lg font-medium">{profile.name}</h3>
            <div className="text-sm text-gray-500">{profile.kitchenSection?.value || ''} {profile.diet?.name ? `• ${profile.diet.name}` : ''}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12"><LoadingSpinner size="lg" /></div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-gray-500">No items in this profile</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto whitespace-nowrap text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-3 py-2 text-left">Item Type</th>
                  <th className="px-3 py-2 text-left">Item</th>
                  <th className="px-3 py-2 text-center" colSpan={3}>XSmall</th>
                  <th className="px-3 py-2 text-center" colSpan={3}>Small</th>
                  <th className="px-3 py-2 text-center" colSpan={3}>Medium</th>
                  <th className="px-3 py-2 text-center" colSpan={3}>Large</th>
                  <th className="px-3 py-2 text-center" colSpan={3}>Extra Large</th>
                  <th className="px-3 py-2 text-center" colSpan={3}>XX Large</th>
                </tr>
                <tr className="bg-gray-50 border-b">
                  <th className="px-3 py-2"></th>
                  <th className="px-3 py-2"></th>
                  {['xs', 's', 'm', 'l', 'xl', 'xxl'].map(size => (
                    <React.Fragment key={size}>
                      <th className="px-3 py-2 text-center text-xs">Weight</th>
                      <th className="px-3 py-2 text-center text-xs">Unit</th>
                      <th className="px-3 py-2 text-center text-xs">Cal</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupedItems.map((group: any[], groupIdx: number) => {
                  const firstItem = group[0];
                  const itemType = firstItem.recipeId ? 'Recipe' : 'Ingredient';
                  const itemName = firstItem.recipe_name || firstItem.ingredient_name || '-';

                  // Create a map of size -> item for quick lookup
                  const sizeItemMap = new Map<string, any>();
                  group.forEach(item => {
                    const size = item.size || 'xs';
                    sizeItemMap.set(size, item);
                  });

                  return (
                    <tr key={groupIdx} className="border-b hover:bg-gray-50">
                      <td className="px-3 py-2">{itemType}</td>
                      <td className="px-3 py-2">{itemName}</td>
                      {['xs', 's', 'm', 'l', 'xl', 'xxl'].map(size => {
                        const item = sizeItemMap.get(size);
                        return (
                          <React.Fragment key={size}>
                            <td className="px-3 py-2 text-center">{item ? item.quantity : '-'}</td>
                            <td className="px-3 py-2 text-center">{item ? (item.mu_abbr || item.mu_name || '-') : '-'}</td>
                            <td className="px-3 py-2 text-center">{item ? item.calories : '-'}</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Inline Create Profile Modal
const CreateProfileModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [dietId, setDietId] = useState('');
  const [ksId, setKsId] = useState('');
  const [status, setStatus] = useState<'ACTIVE'|'INACTIVE'>('ACTIVE');
  const [error, setError] = useState<string | null>(null);

  const { data: dietsData } = useQuery({
    queryKey: ['diets', 'for-select'],
    queryFn: () => dietService.getDiets({ page: 1, limit: 1000, status: 'ACTIVE' as any }),
  });
  const { data: ksData } = useQuery({
    queryKey: ['lookup', 'kitchen-sections'],
    queryFn: () => lookupTableService.getLookupTables({ page: 1, limit: 1000, category: 'KITCHEN_SECTION', status: 'ACTIVE' }),
  });

  const createMutation = useMutation({
    mutationFn: () => dietService.createRecipePlateProfile({ name: name.trim(), dietId: dietId || undefined, kitchenSectionId: ksId || undefined, status }),
    onSuccess: () => onSuccess(),
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to create profile'),
  });

  const diets = (dietsData as any)?.diets || [];
  const kitchenSections = (ksData as any)?.lookupTables || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Create Recipe Plate Profile</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        {error && <div className="mb-3 rounded bg-red-50 p-3 text-sm text-red-700">{error}</div>}
        <div className="space-y-4">
          <div>
            <label className="form-label">Name *</label>
            <input className="form-input" value={name} onChange={e=>setName(e.target.value)} placeholder="Profile name" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Diet</label>
              <select className="form-input" value={dietId} onChange={e=>setDietId(e.target.value)}>
                <option value="">Select diet</option>
                {diets.map((d: any) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Kitchen Section</label>
              <select className="form-input" value={ksId} onChange={e=>setKsId(e.target.value)}>
                <option value="">Select kitchen section</option>
                {kitchenSections.map((k: any) => <option key={k.id} value={k.id}>{k.value}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="form-label">Status</label>
            <select className="form-input" value={status} onChange={e=>setStatus(e.target.value as any)}>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={createMutation.isPending}>Cancel</Button>
          <Button onClick={() => { setError(null); if (!name.trim()) { setError('Name is required'); return; } createMutation.mutate(); }} loading={createMutation.isPending}>Create</Button>
        </div>
      </div>
    </div>
  );
};

export default RecipePlatesPage;
